import { SIZE_MAP } from 'shared/enums/Size';
import { appFetch } from 'shared/utils/appFetch';

export function appendZero(value: string): string {
  if (/^-?\d+$/.test(value)) {
    return `${value}.0`;
  }
  return value;
}

export const getSearchFilterArray = (query: string) => {
  if (!query) return [];
  const searchFilterArray: any[] = [];

  if (query) {
    const searchFilter = query.split(',');
    searchFilter.forEach((filter: any) => {
      if (filter?.length > 0) searchFilterArray.push(filter);
    });
  }

  return searchFilterArray;
};

export const getMinAndMaxPrices = (ranges: string[]): { min: number; max: number | undefined } => {
  if (ranges.length === 0) {
    return {
      min: undefined,
      max: undefined,
    };
  }
  let min = Infinity;
  let max = -Infinity;
  let hasMinOne = false;
  let hasMaxInfinity = false;

  for (const range of ranges) {
    const [lowStr, highStr] = range.split('-');

    if (range === '100-9900') {
      hasMinOne = true;
    }

    const low = Number.parseInt(lowStr, 10);
    const high = highStr === 'inf' ? undefined : Number.parseInt(highStr, 10);

    if (!Number.isNaN(low) && low < min) min = low;

    if (highStr === 'inf') {
      hasMaxInfinity = true;
    } else if (!Number.isNaN(high) && high > max) max = high;
  }

  return {
    min: hasMinOne ? 1 : min,
    max: hasMaxInfinity ? undefined : max,
  };
};

export const isSortablelist = (listTitle: any) => {
  return ['brandsList', 'releaseYearsList'].includes(listTitle);
};

export const getSortedFilterList = (arr: any[] = [], title: string) => {
  if (!isSortablelist(title) || !arr || arr.length <= 1) return arr;
  return arr.sort((a, b) => {
    if (title === 'releaseYearsList') {
      return `${b.name || b}`.localeCompare(a.name || a, undefined, {
        numeric: true,
      });
    }
    return `${a.name || a}`.localeCompare(b.name || b, undefined, {
      numeric: true,
    });
  });
};

export const getTimeStamps = ({ year, month = 0, day = 1 }: any) => {
  return new Date(year, month, day).getTime();
};

export const getYearsFromRange = (range: string) => {
  const [start, end] = range.split('-');
  const currentYear = start ? new Date(Number.parseInt(start, 10)).getFullYear() : undefined;
  const endYear = end ? new Date(Number.parseInt(end, 10)).getFullYear() : undefined;

  return {
    start: currentYear,
    end: endYear,
  };
};

export const getPricesFromRange = (range: string) => {
  const [start, end] = range.split('-');
  return {
    start: start ? Number.parseInt(start, 10) : undefined,
    end: end ? Number.parseInt(end, 10) : undefined,
  };
};

export const getYearsRanges = (end: number) => {
  const currentYear = new Date().getFullYear();
  const startYear = 1985;
  const interval = 5;
  const ranges: any = {};
  let startInterval = startYear;

  ranges[`${getTimeStamps({ year: 1970 })}-${getTimeStamps({ year: 1985 })}`] = {
    start: 1970,
    end: 1985,
  };
  while (startInterval < currentYear) {
    const endInterval = Math.min(startInterval + interval, currentYear);
    const currentItemValue = `${getTimeStamps({
      year: startInterval,
    })}-${getTimeStamps({ year: endInterval })}`;

    ranges[currentItemValue] = {
      start: startInterval,
      end: endInterval,
    };
    startInterval = endInterval; // Set the next interval's start to the year immediately following the end of the previous interval
  }

  ranges[`${getTimeStamps({ year: currentYear })}-${getTimeStamps({ year: end })}`] = {
    start: currentYear,
    end,
  };

  return ranges;
};

export const getPriceRanges = () => {
  // todo: from the min and max price, create 6 ranges
  const ranges: any = {};

  ranges['1-100'] = {
    start: 1,
    end: 100,
  };
  ranges['100-200'] = {
    start: 100,
    end: 200,
  };
  ranges['200-500'] = {
    start: 200,
    end: 500,
  };
  ranges['500-1000'] = {
    start: 500,
    end: 1000,
  };
  ranges['1000-10000'] = {
    start: 1000,
    end: 10000,
  };

  ranges['10000-100000'] = {
    start: 10000,
  };

  return ranges;
};

export const colorsPalette = [
  'white',
  'grey',
  'black',
  'green',
  'teal',
  'blue',
  'purple',
  'pink',
  'red',
  'orange',
  'yellow',
  'cream',
  'tan',
  'brown',
  'silver',
  'gold',
  'copper',
  'multi-color',
];

export function mergeColorsByName(data) {
  const colorMap = new Map();

  for (const item of data) {
    const normalizedName = item.name.trim().toLowerCase();

    if (colorMap.has(normalizedName)) {
      colorMap.get(normalizedName).count += item.count;
    } else {
      colorMap.set(normalizedName, {
        name: normalizedName,
        count: item.count,
      });
    }
  }

  return Array.from(colorMap.values());
}

export const colorSwatch = {
  cream: 'rgb(251, 244, 220)',
  white: '#ececec',
  copper: 'rgb(196, 126, 90)',
};
export const getRangesFromYears = (years: any) => {
  const sortedYears = years.map(Number).sort((a: number, b: number) => a - b);
  const ranges = [];

  const splitRanges: Set<string> = new Set([]);

  if (sortedYears[0] === 1970) {
    // find 1985
    const firstIndex = sortedYears.indexOf(1985);

    if (firstIndex > 0) {
      splitRanges.add(
        `${getTimeStamps({ year: sortedYears[0] })}-${getTimeStamps({
          year: 1985,
        })}`,
      );
      // remove 1970 - 1985
      sortedYears.splice(0, firstIndex);
    }
  }

  let startYear = sortedYears[0];
  let endYear = startYear;

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i < sortedYears.length; i++) {
    if (sortedYears[i] <= endYear + 1) {
      endYear = sortedYears[i];
    } else {
      ranges.push([startYear, endYear]);
      // eslint-disable-next-line no-multi-assign
      startYear = endYear = sortedYears[i];
    }
  }

  ranges.push([startYear, endYear]);

  // const moreSplitRanges = ranges.reduce((acc, [start, end]) => {
  //   if (end - start < 5) {
  //     splitRanges.add(`${getTimeStamps({ year: start })}-${getTimeStamps({ year: end })}`);
  //   } else {
  //     while (end - start >= 5) {
  //       splitRanges.add(
  //         `${getTimeStamps({ year: start })}-${getTimeStamps({
  //           year: start + 5,
  //         })}`,
  //       );
  //       start += 5;
  //     }
  //   }
  //   return acc;
  // }, new Set<string>([]));

  return splitRanges;
};

export const getUpcomingRange = () => {
  const todayYear = new Date();
  const nextYear = new Date();

  // seconds * minutes * hours * milliseconds = 1 day
  const day = 60 * 60 * 24 * 1000;

  const finalToday = new Date(todayYear.getTime() + day);

  nextYear.setFullYear(finalToday.getFullYear() + 1);

  const todayDate = {
    year: finalToday.getFullYear(),
    month: finalToday.getMonth(),
    day: finalToday.getDate(),
  };

  const nextDate = {
    year: nextYear.getFullYear(),
    month: nextYear.getMonth(),
    day: nextYear.getDate(),
  };

  const upcomingRange = `${getTimeStamps(todayDate)}-${getTimeStamps(nextDate)}`;
  return {
    start: todayDate,
    end: nextDate,
    value: upcomingRange,
  };
};

export const getAllYears = (ranges: Set<string>) => {
  const years: Set<string> = new Set([]);
  const upcoming = getUpcomingRange();

  const arr = [...ranges];
  if (arr.length === 0) return {};

  // got specific date? then it's releasedate
  // else it's a bbunch of years
  if (arr[0] === upcoming.value) {
    return {
      releaseDateStart: `${getTimeStamps(upcoming.start)}`,
      releaseDateEnd: `${getTimeStamps(upcoming.end)}`,
    };
  }
  for (const range of ranges) {
    // eslint-disable-next-line prefer-const
    let { start, end }: any = getYearsFromRange(range);
    while (start <= end) {
      years.add(start);
      // eslint-disable-next-line no-plusplus
      start++;
    }
  }

  return {
    releaseYears: [...years].join(','),
  };
};

export const getAllfilters = async ({
  queryString,
  collectionSlug,
}: {
  queryString?: string;
  collectionSlug?: string;
} = {}) => {
  const filtersParams = {
    categories: getSearchFilterArray('' as string),
    releaseYears: getSearchFilterArray('' as string),
    brands: getSearchFilterArray('' as string),
    colors: getSearchFilterArray('' as string),
    genders: getSearchFilterArray('' as string),
    productTypes: [],
  };

  const query = {
    salesChannelId: '1',
    queryString: queryString || '',
    sortType: '1',
    pageLimit: 1,
    pageNumber: 1,
    includeAggregations: 'true',
    collectionSlug: collectionSlug || '',
    productFilter: JSON.stringify(filtersParams),
  };

  const url = `/get-product-search-results?${new URLSearchParams(query as any).toString()}`;
  const results = await appFetch(url, {
    method: 'GET',
  }).catch(() => {
    // console.log('error fetching data');
  });

  return results?.data?.availableFilters || [];
};

export function generateKey(productType, gender, size, unit = 'universal') {
  return `${productType === 'shoes' ? 'us' : unit}_${productType}_${gender}_${(size === 'ONE SIZE' ? 'one_size' : size).toLowerCase()}`.toLowerCase();
}
const fetchSizeData = async () => {
  try {
    const response = await fetch('https://client-metadata-cdn.labs.goateng.com/search_sizes.json');
    if (!response.ok) {
      return {};
    }
    const data = await response.json();
    return data;
  } catch {
    return {};
  }
};

function findSize(chart, gender, type, unit, size) {
  const prType = type === 'shoes' ? 'sneakers' : type;

  const chartForType =
    chart?.[prType]?.[gender]?.find((arrSizes) => {
      return arrSizes.find((itm) => {
        const lowercaseUnit = itm.unit.toLowerCase();
        return itm.size === size && lowercaseUnit === unit.toLowerCase();
      });
    }) || [];

  const universalSize = chartForType?.find((r) =>
    type === 'shoes' ? r.unit === 'us' : r.unit === 'universal',
  );

  if (!universalSize) {
    const uni = chartForType?.find((r) => r.unit === 'us') || size;

    return { universalSize: uni, chartForType };
  }

  return { universalSize, chartForType };
}
export async function transformConsumerSearchSizeData(array) {
  const result = {};

  const chartSize = await fetchSizeData().then((r) => r);

  array.forEach((item) => {
    const productType = item.name;

    if (['shoes', 'tops', 'bottoms', 'outerwear', 'dresses', 'belts'].includes(item.name)) {
      if (!result[productType]) {
        result[productType] = {};
      }

      item.gendersList.forEach((genderItem) => {
        const [countryRaw, genderRaw] = genderItem.name.split('_');
        const gender = genderRaw.toLowerCase();
        const country = countryRaw.toLowerCase();

        if (!result[productType][gender]) {
          result[productType][gender] = [];
        }

        // Use a map to deduplicate by productType + gender + size
        const sizeMap = {};

        // Load existing entries
        result[productType][gender].forEach((entry) => {
          sizeMap[entry.value] = entry;
        });

        // Merge or add new entries
        genderItem.availableSizesList.forEach((sizeObj) => {
          const size = sizeObj.name;

          const { universalSize: currentSizeUniversal, chartForType } = findSize(
            chartSize,
            gender,
            productType,
            country,
            size,
          ) || { currentSizeUniversal: { size, unit: country.toLowerCase() }, chartForType: [] };
          const key = generateKey(
            productType,
            gender,
            currentSizeUniversal?.size || size,
            currentSizeUniversal?.unit as string,
          );

          let sizingInfo = {};
          if (chartForType.length > 0) {
            chartForType.forEach((itm) => {
              sizingInfo[itm.unit] = {
                [item.name]: {
                  [gender]:
                    itm.size === '200'
                      ? 'one size'
                      : productType === 'shoes'
                        ? appendZero(itm.size)
                        : // eslint-disable-next-line unicorn/explicit-length-check
                          SIZE_MAP[size] || appendZero(itm.size),
                },
              };
            });
          } else {
            sizingInfo = {
              [country]: {
                [item.name]: {
                  [gender]:
                    size === '200'
                      ? 'one size'
                      : productType === 'shoes'
                        ? appendZero(size)
                        : SIZE_MAP[size] || appendZero(size),
                },
              },
            };
          }

          const newEntry = {
            count: sizeObj.count,
            display_name:
              SIZE_MAP[currentSizeUniversal.size] || (currentSizeUniversal.size as any) || size,
            value: key,
            data: {
              gender,
              product_type: item.name === 'shoes' ? 'sneakers' : item.name,
              sizing_info: sizingInfo,
            },
          };

          sizeMap[key] = newEntry;
        });

        result[productType][gender] = Object.values(sizeMap);
        if (result[productType][gender].length === 0) {
          delete result[productType][gender];
        }
      });
    }
  });

  return result;
}

// facets for price buckets
export const PRICE_BUCKETS_FACET = {
  display_name: 'Price',
  name: 'lowest_price_cents_bucket',
  type: 'multiple',
  hidden: false,
  data: {
    default_buckets: [
      'Under $100',
      '$100 - $200',
      '$200 - $500',
      '$500 - $1000',
      '$1000 - $2500',
      '$2500 - $5000',
      '$5000+',
    ],
  },
  options: [
    {
      status: '',
      count: 5791,
      display_name: 'Under $100',
      value: '"100"-"9900"',
      data: {
        mobile_display_name: 'Under $100',
      },
      range: [100, 9900],
    },
    {
      status: '',
      count: 4387,
      display_name: '$100 - $200',
      value: '"10000"-"20000"',
      data: {
        mobile_display_name: '$100 - $200',
      },
      range: [10000, 20000],
    },
    {
      status: '',
      count: 7755,
      display_name: '$200 - $500',
      value: '"20000"-"50000"',
      data: {
        mobile_display_name: '$200 - $500',
      },
      range: [20000, 50000],
    },
    {
      status: '',
      count: 7026,
      display_name: '$500 - $1000',
      value: '"50000"-"100000"',
      data: {
        mobile_display_name: '$500 - $1000',
      },
      range: [50000, 100000],
    },
    {
      status: '',
      count: 295,
      display_name: '$1000 - $2500',
      value: '"100000"-"250000"',
      data: {
        mobile_display_name: '$1000 - $2500',
      },
      range: [100000, 250000],
    },
    {
      status: '',
      count: 211,
      display_name: '$2500 - $5000',
      value: '"250000"-"500000"',
      data: {
        mobile_display_name: '$2500 - $5000',
      },
      range: [250000, 500000],
    },
    {
      status: '',
      count: 14,
      display_name: '$5000 - $10000',
      value: '"500000"-"1000000"',
      data: {
        mobile_display_name: '$5000 - $10000',
      },
      range: [500000, 1000000],
    },
    {
      status: '',
      count: 9,
      display_name: '$10000+',
      value: '"1000000"-"inf"',
      data: {
        mobile_display_name: '$10000+',
      },
      range: [1000000, 'inf'],
    },
  ],
};
